// src/components/sections/EtHowItWorks.jsx
import { motion } from "framer-motion";
import {
  PencilSquareIcon,
  SparklesIcon,
  Cog6ToothIcon,
} from "@heroicons/react/24/outline";

const steps = [
  {
    icon: PencilSquareIcon,
    title: "Capture",
    desc: "Begin with guided introspection — capture your thoughts, patterns, and reflections with the help of human guidance and AI prompts.",
  },
  {
    icon: SparklesIcon,
    title: "Insight",
    desc: "Transform chaos into clarity. Trinity helps uncover patterns, crystallize purpose, and align priorities for meaningful growth.",
  },
  {
    icon: Cog6ToothIcon,
    title: "Execute",
    desc: "Turn clarity into consistency. Build purpose-led habits, track your performance, and sustain progress through Trinity’s AI-powered workflows.",
  },
];

export default function EtHowItWorks({ctaVariant = "trinity"}) {
  // First Created - 15 Aug 2025
  // Author - Atul Pandey
  // Updated - 31 Oct 2025 - for TrinityHome
  // Updated - 11 Nov 2025 - added prop - ctaVariant
  // Updated - 20 Nov 2025 - renamed section id to "how-it-works"

  return (
    <section
      // id="approach"
      id="how-it-works" // 20 Nov 2025
      className="py-20 bg-gradient-to-b from-black via-orange-900 to-black text-white relative overflow-hidden"
    >
      {/* Subtle flowing gradient backdrop */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,115,0,0.15)_0%,transparent_70%)] animate-pulse" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Section Heading */}
        <motion.h2
          className="text-3xl md:text-4xl font-bold mb-4 text-orange-600"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          From Chaos to Clarity to Consistency
        </motion.h2>

        <motion.p
          className="text-gray-300 max-w-3xl mx-auto mb-12 text-lg font-light"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          Trinity begins with guided human introspection. Helping leaders cut
          through the noise, focus on what truly matters, and build purpose-led
          growth habits powered by AI.
        </motion.p>

        {/* Transition Bar: Chaos → Clarity → Consistency */}
        <motion.div
          className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 mb-16"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.8 }}
        >
          {["Chaos", "Clarity", "Consistency"].map((stage, index) => (
            <div key={stage} className="flex items-center gap-3">
              <motion.div
                className="h-3 w-3 rounded-full bg-orange-500 shadow-lg"
                animate={{ scale: [1, 1.3, 1], opacity: [0.7, 1, 0.7] }}
                transition={{ repeat: Infinity, duration: 3, delay: index * 0.3 }}
              />
              <span className="text-gray-300 font-semibold tracking-wide uppercase text-sm">
                {stage}
              </span>
              {index < 2 && (
                <div className="w-10 h-[1px] bg-gradient-to-r from-orange-600 to-transparent" />
              )}
            </div>
          ))}
        </motion.div>

        {/* Core Trinity Steps */}
        <div className="grid md:grid-cols-3 gap-12 relative">
          {steps.map((step, index) => (
            <motion.div
              key={step.title}
              className="flex flex-col items-center text-center relative"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.3, duration: 0.7 }}
            >
              {/* Icon */}
              <div className="flex items-center justify-center h-20 w-20 rounded-full bg-orange-600 shadow-lg mb-6">
                <step.icon className="h-10 w-10 text-white" />
              </div>

              {/* Title + Description */}
              <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
              <p className="text-gray-300 text-md max-w-sm">{step.desc}</p>

              {/* Connector Line (Desktop only) */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-10 right-[-60px] w-[120px] h-[2px] bg-gradient-to-r from-orange-600 to-transparent"></div>
              )}
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        { ctaVariant === "trinity" && (
          
          <motion.div
            className="mt-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            <a
              href="#contact"
              className="inline-flex items-center gap-2 px-6 py-3 bg-orange-600 text-white font-semibold rounded-lg shadow hover:bg-orange-700 transition"
            >
              Experience the Trinity Approach
            </a>
          </motion.div> 
        )}
      </div>
    </section>
  );
}